import {
  Book,
  Check,
  Copy,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Square,
  ThumbsDown,
  ThumbsUp,
  Volume2,
  Wrench,
} from 'lucide-react';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { AttachmentList } from './AttachmentChip';
import { AccountDraftCard } from './AccountDraftCard';
import { BuildProgressChecklist, formatElapsed } from './BuildProgressChecklist';
import { ChoiceCard } from './ChoiceCard';
import { NavigateCard } from './NavigateCard';
import { BacktestReportCard } from './BacktestReportCard';
import { BacktestStartedCard } from './BacktestStartedCard';
import { BacktestConfirmCard } from './BacktestConfirmCard';
import { ClientConfirmCard } from './ClientConfirmCard';
import { CrmProposalCard } from './CrmProposalCard';
import { RevealingMarkdown } from './RevealingMarkdown';
import { StrategyCreatedCard } from './StrategyCreatedCard';
import type {
  AccountDraftCard as AccountDraftCardData,
  BacktestConfirm,
  BacktestReport,
  BacktestStarted,
  ChatAttachment,
  ChoiceGroup,
  ClientConfirmCard as ClientConfirmCardData,
  CreatedStrategy,
  CrmProposalCard as CrmProposalCardData,
  NavigateCard as NavigateCardData,
  SoteriaBuildStep,
  SoteriaToolEvent,
} from '../hooks/useSoteriaChat';
import type {ReactNode} from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Steps that read or search something, which get the book icon rather than the
 * generic wrench. Names must match the backend's tool names exactly (see
 * agent_output._TOOL_LABELS) — the previous check used a name no tool has, so
 * the icon was unreachable.
 */
const READING_TOOLS = new Set<string>([
  'knowledge_base_search_tool',
  'platform_docs_tool',
  'onboarding_forms_tool',
  'web_search_tools',
  'get_current_strategy_tool',
  'get_backtest_report_tool',
  'list_clients_tool',
  'find_client_tool',
  'get_client_tool',
  'list_account_types_tool',
]);

export interface SoteriaMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  // Live "thinking" activity while the reply is pending; cleared on completion.
  stage?: string;
  timestamp: Date;
  rating?: 'up' | 'down';
  tools?: Array<SoteriaToolEvent>;
  // Determinate progress for a turn that is building a strategy: the whole
  // step list, plus how long it has been going. Present only while running.
  buildSteps?: Array<SoteriaBuildStep>;
  elapsedSeconds?: number;
  strategy?: CreatedStrategy;
  backtest?: BacktestStarted;
  // "Run backtest" prompt on the turn that built a strategy — a build never
  // launches one itself.
  backtest_confirm?: BacktestConfirm;
  reports?: Array<BacktestReport>;
  client?: ClientConfirmCardData;
  account_draft?: AccountDraftCardData;
  // CRM writes the assistant proposed this turn; each waits for the advisor's
  // Confirm and saves nothing until then.
  crm_proposals?: Array<CrmProposalCardData>;
  navigation?: Array<NavigateCardData>;
  choices?: Array<ChoiceGroup>;
  // Files sent with this turn (a user message). Rendered as chips in the same
  // bubble as the text, so an attachment reads as part of the message rather
  // than a separate event.
  attachments?: Array<ChatAttachment>;
}

interface SoteriaMessageListProps {
  messages: Array<SoteriaMessage>;
  isStreaming?: boolean;
  streamingMessageId?: string | null;
  copiedId?: string | null;
  onCopy: (id: string, content: string) => void;
  onRegenerate?: () => void;
  onRate?: (id: string, rating: 'up' | 'down') => void;
  /** The portal does not use the advisor TTS endpoint. */
  canReadAloud?: boolean;
  // Clicking a card's button sends text as the next turn — the strategy
  // builder's option buttons, and the "Run backtest" confirmation.
  onChoose?: (value: string) => void;
  // The chat the cards belong to. A proposal is bound to the chat that made it,
  // so its Confirm / Cancel need this id to be accepted.
  sessionId?: string | null;
}

/**
 * SoteriaMessageList
 * ------------------
 * The conversation transcript, tuned to a ChatGPT / Claude reading rhythm:
 * user turns sit in a soft right-aligned bubble; assistant turns are plain
 * full-width prose with no avatar or name — the answer is the answer, and the
 * two speakers are already unambiguous. Each turn fades in as it arrives
 * (respecting reduced-motion), and turns are separated by whitespace rather
 * than hard rules. All colors are tokens, so the transcript adapts to dark
 * mode.
 *
 * Action bar (Listen / Copy / Regenerate / thumbs): always visible on the
 * latest reply, hover-revealed on older ones; every icon action carries a
 * tooltip.
 */
/** Shown until the backend reports a real stage — every wait starts here.
 *  "thinking" told the user nothing and, on a slow first token, looked stuck. */
const STREAMING_FALLBACK_STAGE = 'Working through your question…';

// Short wall-clock time for the hover timestamp; guards against a missing or
// invalid date (history loads stamp new Date()).
export function SoteriaMessageList({
  messages,
  streamingMessageId,
  copiedId,
  onCopy,
  onRegenerate,
  onRate,
  onChoose,
  sessionId,
  canReadAloud = true,
}: SoteriaMessageListProps) {
  const tts = useTextToSpeech();

  // The newest assistant turn — the only one Regenerate may act on. Found by
  // scanning rather than assuming it is the last message, so a transcript that
  // ends on a user turn (a question still being answered) offers it on nothing
  // rather than on the wrong thing.
  const lastAssistantIndex = messages.reduce(
    (found, m, i) => (m.role === 'assistant' ? i : found),
    -1,
  );

  // Strip Markdown noise out of the text we hand to TTS — asterisks,
  // backticks, link syntax, headings — so the engine voices only the
  // prose. Keep numbers/units intact so percentages and dates read
  // naturally.
  const ttsClean = (raw: string): string =>
    raw
      .replace(/```[\s\S]*?```/g, ' code block ')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/!?\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, '$1')
      .replace(/^[->]\s+/gm, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

  return (
    <TooltipProvider delayDuration={300}>
      <div className="mx-auto w-full max-w-3xl px-1 pb-12 pt-10">
        {messages.map((message, index) => {
          const isAssistant = message.role === 'assistant';
          const isStreaming = streamingMessageId === message.id;
          const isLast = index === messages.length - 1;
          // Regenerate acts on the LAST turn and nothing else: the server hides
          // the last exchange and hands the prompt back (POST
          // /history/{id}/regenerate → supersede_last_turn). The button used to
          // render on every assistant message while calling that same
          // last-turn-only endpoint, so clicking it on an older reply silently
          // replaced the newest one instead — the user watched a different
          // message than the one they aimed at disappear.
          const canRegenerate = index === lastAssistantIndex;
          // First assistant turn of a run gets the full turn gap above it;
          // a continuation turn tucks under the one before it.
          const startsRun =
            isAssistant && messages[index - 1]?.role !== 'assistant';
          return (
            <div
              key={message.id}
              className={cn(
                'group/message duration-300 animate-in fade-in motion-reduce:animate-none',
                isAssistant && !startsRun ? 'pt-1 pb-8' : 'py-8',
              )}
            >
              {/* No per-turn avatar or name. Labelling every reply "Soteria"
                  turns the column into a feed of posts from a bot; the
                  reference surfaces just let the answer be the answer. The two
                  speakers are already unambiguous — you are the filled bubble
                  on the right, the assistant is the full-width prose on the
                  left — and the assistant's identity lives in the page chrome
                  and the empty state, where it is said once. */}

              {/* What the model is DOING, while it does it — the backend's live
                  report_stage ("Searching the knowledge base…", "Analyzing the
                  strategy…"). A long wait reads as a hang when nothing changes;
                  the same wait reads as work when it narrates itself. */}
              {/* A build has a KNOWN plan, so it gets the determinate
                  checklist instead of the indeterminate line — the whole
                  difference between "something is happening" and "you are
                  three steps of five in, four minutes deep". Every other kind
                  of turn keeps the dots: their work genuinely has no
                  enumerable end, and a fake progress bar would be worse than
                  none. */}
              {isStreaming && message.buildSteps?.length ? (
                <BuildProgressChecklist
                  steps={message.buildSteps}
                  elapsedSeconds={message.elapsedSeconds}
                />
              ) : isStreaming ? (
                <div
                  className="mb-3 flex items-center gap-2.5 text-sm font-medium text-foreground"
                  role="status"
                  aria-live="polite"
                >
                  <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground motion-reduce:animate-none" />
                    <span
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground motion-reduce:animate-none"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground motion-reduce:animate-none"
                      style={{ animationDelay: '300ms' }}
                    />
                  </span>
                  <span>{message.stage ?? STREAMING_FALLBACK_STAGE}</span>
                  {message.elapsedSeconds != null &&
                    message.elapsedSeconds >= 20 && (
                      // Only once a turn is long enough that the wait is worth
                      // acknowledging. On a quick answer a stopwatch just draws
                      // attention to a delay nobody noticed.
                      <span
                        className="text-xs tabular-nums font-normal text-muted-foreground"
                        aria-hidden="true"
                      >
                        {formatElapsed(message.elapsedSeconds)}
                      </span>
                    )}
                </div>
              ) : null}

              {/* Tool steps — quiet metadata, not callouts. These are the
                  assistant's working notes: a filled brand-tinted pill per step
                  turned a four-step turn into a wall of colour that outweighed
                  the answer itself. Muted text behind a hairline rule reads as
                  "here is what I did" and lets the reply lead. */}
              {message.tools && message.tools.length > 0 && (
                <div className="mb-3.5 space-y-0.5 border-l border-border pl-3">
                  {message.tools.map((t, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 py-0.5 text-xs text-muted-foreground"
                    >
                      {READING_TOOLS.has(t.tool) ? (
                        <Book className="h-3.5 w-3.5 shrink-0 opacity-70" />
                      ) : (
                        <Wrench className="h-3.5 w-3.5 shrink-0 opacity-70" />
                      )}
                      {/* Never falls back to `t.tool`. That printed the raw
                          internal name — "delegate task to member" told users
                          there is a team of agents behind Soteria, which is our
                          architecture and not something the product should
                          announce. The backend curates every label, so an
                          unlabelled step degrades to a vague line instead. */}
                      <span className="min-w-0 truncate">
                        {t.summary || 'Working on it'}
                      </span>
                      {!t.result && !t.error && (
                        <Loader2 className="h-3 w-3 shrink-0 animate-spin opacity-60 motion-reduce:animate-none" />
                      )}
                      {t.result && (
                        <Check className="h-3.5 w-3.5 shrink-0 text-success/80" />
                      )}
                      {/* Soft handling — never surface raw error text. The
                          backend sends "retrying" for recoverable failures. */}
                      {t.error && (
                        <span className="shrink-0 text-warning">· retrying</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Message body. The reply is streamed but delivered by polling,
                  so it can only land in ~1.2s jumps — RevealingMarkdown pays
                  those chunks out word by word so it reads as typing instead of
                  blocks appearing. Display-only: message.content stays whole, so
                  copy / TTS / history are unaffected. */}
              {/* Files sent WITH this message, above its text — attached to the
                turn rather than logged as a separate event. They sit outside the
                bubble, which is the convention on both reference surfaces and
                keeps a wide chip from stretching the bubble. */}
            {message.attachments?.length ? (
              <AttachmentList attachments={message.attachments} />
            ) : null}

            {isAssistant ? (
                <RevealingMarkdown
                  content={message.content}
                  streaming={isStreaming}
                />
              ) : message.content ? (
                <div className="flex justify-end">
                  <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl bg-muted px-4 py-3 text-[16px] leading-[1.7] text-foreground">
                    {message.content}
                  </div>
                </div>
              ) : null}
              {/* No bubble when there is no text. A turn can legitimately be
                  just a file — drag an image in, press send, type nothing — and
                  the bubble used to render regardless, so that turn drew an
                  empty grey box under the picture. It is only visible now
                  because the API stopped dropping those turns from the
                  transcript; before that they vanished on reload and the empty
                  box only ever flashed up in the live session. */}

              {message.strategy && (
                <StrategyCreatedCard strategy={message.strategy} />
              )}
              {message.backtest && (
                <BacktestStartedCard backtest={message.backtest} />
              )}
              {/* Only on the latest turn: an old reply's Run button would start
                  a backtest on a strategy the conversation has moved past. */}
              {message.backtest_confirm &&
                onChoose &&
                index === messages.length - 1 && (
                  <BacktestConfirmCard
                    card={message.backtest_confirm}
                    onConfirm={onChoose}
                  />
                )}
              {message.reports?.map((r, i) => (
                // Cards de-dupe by (strategy_id, report_tab), so one turn can
                // show two cards for the same strategy on different tabs.
                <BacktestReportCard
                  key={`${r.strategy_id ?? i}-${r.report_tab ?? ''}`}
                  report={r}
                />
              ))}
              {message.client && <ClientConfirmCard client={message.client} />}
              {/* One card per destination. The backend drops any destination a
                  card above already opens, so these never echo a report card. */}
              {message.navigation?.map((card, i) => (
                <NavigateCard key={`${card.path}-${i}`} card={card} />
              ))}
              {message.account_draft && (
                <AccountDraftCard
                  draft={message.account_draft}
                  sessionId={sessionId}
                />
              )}
              {message.crm_proposals?.map((proposal) => (
                <CrmProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  sessionId={sessionId}
                />
              ))}
              {/* Strategy-builder "choose one" options — shown ONLY on the
                  latest turn; once picked (or a new turn arrives) they
                  disappear, so the transcript never piles up dead button rows. */}
              {message.choices &&
                message.choices.length > 0 &&
                onChoose &&
                index === messages.length - 1 && (
                  <ChoiceCard groups={message.choices} onChoose={onChoose} />
                )}

              {/* Action bar — always visible on the latest reply, hover-revealed
                  on older ones. */}
              {isAssistant && !isStreaming && message.content && (
                <div
                  className={cn(
                    'mt-4 flex items-center gap-1 transition-opacity duration-200',
                    isLast
                      ? 'opacity-100'
                      : 'opacity-0 focus-within:opacity-100 group-hover/message:opacity-100',
                  )}
                >
                  {/* Icon-only, same footprint as its neighbours. A bordered
                      "Listen" pill next to four bare icon buttons made the row
                      look assembled from two different kits. */}
                  {canReadAloud && tts.supported && (
                    <IconAction
                      label={
                        tts.isLoading
                          ? 'Generating voice…'
                          : tts.isSpeaking
                            ? 'Stop reading'
                            : 'Listen to response'
                      }
                      onClick={() => {
                        if (tts.isSpeaking || tts.isLoading) tts.stop();
                        else tts.speak(ttsClean(message.content));
                      }}
                    >
                      {tts.isLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground motion-reduce:animate-none" />
                      ) : tts.isSpeaking ? (
                        <Square className="h-3.5 w-3.5 fill-current text-primary" />
                      ) : (
                        <Volume2 className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </IconAction>
                  )}
                  {canReadAloud && tts.supported && tts.isSpeaking && (
                    <IconAction
                      label={tts.isPaused ? 'Resume' : 'Pause'}
                      onClick={() => (tts.isPaused ? tts.resume() : tts.pause())}
                    >
                      {tts.isPaused ? (
                        <Play className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <Pause className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </IconAction>
                  )}
                  {canReadAloud && tts.supported && (
                    <div className="mx-0.5 h-3.5 w-px bg-border" />
                  )}
                  <IconAction
                    label="Copy"
                    onClick={() => onCopy(message.id, message.content)}
                  >
                    {copiedId === message.id ? (
                      <Check className="h-3.5 w-3.5 text-success" />
                    ) : (
                      <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </IconAction>
                  {canRegenerate && onRegenerate && (
                    <IconAction label="Regenerate" onClick={onRegenerate}>
                      <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                    </IconAction>
                  )}
                  {onRate && (
                    <>
                      <div className="mx-0.5 h-3.5 w-px bg-border" />
                      <IconAction
                        label="Good response"
                        onClick={() => onRate(message.id, 'up')}
                      >
                        <ThumbsUp
                          className={cn(
                            'h-3.5 w-3.5',
                            message.rating === 'up'
                              ? 'text-success'
                              : 'text-muted-foreground',
                          )}
                        />
                      </IconAction>
                      <IconAction
                        label="Bad response"
                        onClick={() => onRate(message.id, 'down')}
                      >
                        <ThumbsDown
                          className={cn(
                            'h-3.5 w-3.5',
                            message.rating === 'down'
                              ? 'text-destructive'
                              : 'text-muted-foreground',
                          )}
                        />
                      </IconAction>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

// Icon action button wrapped in a tooltip — the action bar's
// copy / regenerate / rate / pause controls.
function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className="rounded-md p-1.5 transition-colors hover:bg-accent"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
